/**
 * Currents Booking Widget — Entry Point
 *
 * This is the self-executing script that:
 * 1. Reads window.currentsConfig
 * 2. Creates a Shadow DOM container
 * 3. Fetches the brand profile from the Currents API
 * 4. Renders the widget (floating button + modal) inside the Shadow DOM
 *
 * Supports two modes:
 *   - "floating" (default): Fixed-position overlay with trigger button
 *   - "inline": Renders directly into a container element on the page
 *
 * Usage (floating — default):
 *
 *   <script>
 *     window.currentsConfig = { clubId: "your-club-id" };
 *   </script>
 *   <script src="https://cdn.currents.golf/widget/v1/currents-widget.js" async></script>
 *
 * Usage (inline):
 *
 *   <div id="currents-booking-widget"></div>
 *   <script>
 *     window.currentsConfig = {
 *       clubId: "your-club-id",
 *       mode: "inline",
 *       container: "#currents-booking-widget"
 *     };
 *   </script>
 *   <script src="https://cdn.currents.golf/widget/v1/currents-widget.js" async></script>
 */

import { render } from 'preact'
import { WidgetRoot } from './components/WidgetRoot'
import { fetchBrandProfile } from './api/brand'
import { DEFAULT_BRAND_PROFILE, type CurrentsConfig } from './types'
import widgetCSS from './styles/widget.css?inline'

const WIDGET_HOST_ID = 'currents-widget-root'

/**
 * Apply brand profile colours as CSS custom properties on the shadow host.
 */
function applyBrandTokens(host: HTMLElement, brand: typeof DEFAULT_BRAND_PROFILE): void {
  host.style.setProperty('--cw-primary', brand.colors.primary)
  host.style.setProperty('--cw-accent', brand.colors.accent)
  host.style.setProperty('--cw-bg', brand.colors.background)
  host.style.setProperty('--cw-text', brand.colors.text)
  host.style.setProperty('--cw-button-text', brand.colors.buttonText)
  host.style.setProperty('--cw-error', brand.colors.error)
  host.style.setProperty('--cw-radius', brand.typography.borderRadius)
  host.style.setProperty('--cw-font', brand.typography.fontFamily)
  host.style.setProperty('--cw-font-heading', brand.typography.headingFontFamily)
}

/**
 * Bootstrap the widget.
 */
async function init(): Promise<void> {
  // Read configuration from host page
  const config: Partial<CurrentsConfig> = window.currentsConfig || {}

  if (!config.clubId) {
    console.warn('[Currents] Missing required config: window.currentsConfig.clubId')
    return
  }

  const env = config.env || 'production'
  const isInline = config.mode === 'inline'

  // ─── Inline mode: render into an existing container element ──────────
  if (isInline) {
    const containerSelector = config.container || '#currents-booking-widget'
    const container = document.querySelector(containerSelector)

    if (!container) {
      console.warn(`[Currents] Inline container not found: ${containerSelector}`)
      return
    }

    // Guard against double-loading in the same container
    if (container.querySelector(`#${WIDGET_HOST_ID}`)) {
      console.warn('[Currents] Widget already initialised in this container.')
      return
    }

    // Create host element inside the container
    const host = document.createElement('div')
    host.id = WIDGET_HOST_ID
    host.style.cssText = 'position:relative;width:100%;'
    container.appendChild(host)

    // Attach Shadow DOM for style isolation
    const shadow = host.attachShadow({ mode: 'open' })

    // Inject widget CSS
    const style = document.createElement('style')
    style.textContent = widgetCSS
    shadow.appendChild(style)

    // Create mount point
    const mountPoint = document.createElement('div')
    mountPoint.style.cssText = 'pointer-events:auto;'
    shadow.appendChild(mountPoint)

    // Fetch brand profile
    const brand = await fetchBrandProfile(config.clubId, env)

    // Inject custom CSS from brand profile
    if (brand.customCSS) {
      const customStyle = document.createElement('style')
      customStyle.textContent = brand.customCSS
      shadow.appendChild(customStyle)
    }

    // Apply brand tokens
    applyBrandTokens(host, brand)

    // Render inline widget (always open, no floating button)
    render(
      <WidgetRoot
        brand={brand}
        skipVerification={config.skipVerification}
        requirePhone={config.requirePhone}
        clubId={config.clubId}
        inline={true}
      />,
      mountPoint
    )

    console.log(`[Currents] Inline widget initialised for ${brand.clubName} (${config.clubId})`)
    return
  }

  // ─── Floating mode (default): fixed-position overlay ─────────────────

  // Guard against double-loading
  if (document.getElementById(WIDGET_HOST_ID)) {
    console.warn('[Currents] Widget already initialised.')
    return
  }

  // Create host element (positioned fixed, above everything)
  const host = document.createElement('div')
  host.id = WIDGET_HOST_ID
  // Minimal inline styles to position the host — the rest is in Shadow DOM CSS
  host.style.cssText = 'position:fixed;z-index:2147483647;top:0;left:0;width:0;height:0;overflow:visible;pointer-events:none;'
  document.body.appendChild(host)

  // Attach Shadow DOM for complete style isolation
  const shadow = host.attachShadow({ mode: 'open' })

  // Inject widget CSS into shadow root
  const style = document.createElement('style')
  style.textContent = widgetCSS
  shadow.appendChild(style)

  // Create mount point for Preact
  const mountPoint = document.createElement('div')
  mountPoint.style.cssText = 'pointer-events:auto;'
  shadow.appendChild(mountPoint)

  // Fetch brand profile (with caching)
  const brand = await fetchBrandProfile(config.clubId, env)

  // Inject custom CSS from brand profile (if any)
  if (brand.customCSS) {
    const customStyle = document.createElement('style')
    customStyle.textContent = brand.customCSS
    shadow.appendChild(customStyle)
  }

  // Apply brand tokens as CSS custom properties
  applyBrandTokens(host, brand)

  // Render the widget with per-club config flags
  render(
    <WidgetRoot
      brand={brand}
      skipVerification={config.skipVerification}
      requirePhone={config.requirePhone}
      clubId={config.clubId}
    />,
    mountPoint
  )

  console.log(`[Currents] Widget initialised for ${brand.clubName} (${config.clubId})`)
}

// ─── Self-execute ───────────────────────────────────────────────────────

// Wait for DOM to be ready, then init
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init)
} else {
  init()
}
